import { createEvent, createStore } from 'effector';
import { createInitialEntityState, EntityState, OrgMember, OrgMemberRole } from '@nx-frontend/shared/types';

export type OrgMembersState = EntityState<OrgMember>;
export const setMembersStateEvent = createEvent<OrgMembersState>();
export const upsertMembersEvent = createEvent<OrgMember[]>();
export const setMembersEvent = createEvent<OrgMember[]>();
export const $membersState = createStore<OrgMembersState>(createInitialEntityState())
  .on(setMembersStateEvent, (_, state) => {
    return { ...state };
  })
  .on(setMembersEvent, (state, members) => {
    const entities: Record<string, OrgMember> = {};
    members.forEach((member) => {
      const id = getMemberId(member);
      entities[id] = member;
    });
    return { entities, ids: Object.keys(entities) };
  })
  .on(upsertMembersEvent, (state, members) => {
    const entities = { ...state.entities };
    members.forEach((member) => {
      const id = getMemberId(member);
      entities[id] = member;
    });
    return { ...state, entities, ids: Object.keys(entities) };
  });

export function membersToState(members: OrgMember[]): OrgMembersState {
  const entities: Record<string, OrgMember> = {};
  members.forEach((member) => {
    const id = getMemberId(member);
    entities[id] = member;
  });
  return { entities, ids: Object.keys(entities) };
}

export function getMemberId(member: OrgMember): string {
  return member.orgId + '-' + member.userId;
}

export const $members = $membersState.map((state) => Object.values(state.entities));
export const $orgMemberUserIds = $members.map((members) => members.map((member) => member.userId));

export function getMemberByIdParams(id: string) {
  return {
    keys: [id],
    store: $membersState,
    fn: (state: OrgMembersState, [keyId]: string[]) => state.entities[keyId],
  };
}

export function getMembersCountFilteredByOrgRoleParams(orgRoles: OrgMemberRole[]) {
  return {
    keys: orgRoles,
    defaultValue: 0,
    store: $membersState,
    fn: (state: OrgMembersState, orgRoles: OrgMemberRole[]) => {
      return Object.values(state.entities).filter((member) => {
        return orgRoles.includes(member.role);
      }).length;
    },
  };
}

export function getMemberRoleByUserId(userId: number) {
  return {
    keys: [userId],
    defaultValue: null,
    store: $membersState,
    fn: (state: OrgMembersState, [userId]: number[]) => {
      if (!userId) {
        return null;
      }
      return Object.values(state.entities).find((member) => {
        return member.userId === userId;
      })?.role;
    },
    updateFilter: (next: OrgMemberRole | null, prev: OrgMemberRole | null) => {
      return next !== prev;
    },
  };
}
